import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { api } from '../services/api';

interface Subject {
  id: number;
  name: string;
  board_id?: number;
  standard_id?: number;
  medium_id?: number;
  board_name?: string;
  standard_name?: string;
  medium_name?: string;
  created_at?: string;
}

interface Board { id: number; name: string; }
interface Standard { id: number; name: string; }
interface SubjectMaster { id: number; name: string; }
interface Chapter { id: number; name: string; }
interface Topic { id: number; name: string; }
interface Stream { id: number; name: string; }
interface Medium { id: number; code: string; name: string; }

const Subjects: React.FC = () => {
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalSubjects, setTotalSubjects] = useState(0);

  // Master data
  const [boards, setBoards] = useState<Board[]>([]);
  const [standards, setStandards] = useState<Standard[]>([]);
  const [streams, setStreams] = useState<Stream[]>([]);
  const [mediums, setMediums] = useState<Medium[]>([]);
  
  // For extra filter cascading visual consistency (if backend supports searching by chapter/topic on subject)
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [topics, setTopics] = useState<Topic[]>([]);

  // Filters
  const [boardFilter, setBoardFilter] = useState('');
  const [mediumFilter, setMediumFilter] = useState('');
  const [standardFilter, setStandardFilter] = useState('');
  const [streamFilter, setStreamFilter] = useState('');
  const [chapterFilter, setChapterFilter] = useState('');
  const [topicFilter, setTopicFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Dialogs
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [subjectToDelete, setSubjectToDelete] = useState<Subject | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [subjectToEdit, setSubjectToEdit] = useState<Partial<Subject>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadMasterData();
  }, []);

  useEffect(() => {
    loadSubjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    rowsPerPage,
    boardFilter,
    mediumFilter,
    standardFilter,
    streamFilter,
  ]);

  const loadMasterData = async () => {
    try {
      const [boardsRes] = await Promise.all([api.master.getBoards()]);
      setBoards(boardsRes.data.boards || []);
    } catch (err) {
      console.error('Failed to load master data:', err);
    }
  };

  const loadMediums = async () => {
    try {
      const response = await api.master.getMediums();
      setMediums(response.data.mediums || []);
    } catch (err) {
      console.error('Failed to load mediums:', err);
    }
  };

  const loadStandards = async (boardId: string) => {
    if (!boardId) {
      setStandards([]);
      return;
    }
    try {
      const response = await api.master.getStandards(parseInt(boardId));
      setStandards(response.data.standards || []);
    } catch (err) {
      console.error('Failed to load standards:', err);
    }
  };

  const loadStreams = async (standardId: string) => {
    if (!standardId) {
      setStreams([]);
      return;
    }
    try {
      const response = await api.master.getStreams(parseInt(standardId));
      setStreams(response.data.streams || []);
    } catch (err) {
      console.error('Failed to load streams:', err);
    }
  };

  const loadSubjects = async () => {
    try {
      setLoading(true);
      // Wait, api.master.getSubjects currently signature: (boardId?: number, standardId?: number, mediumId?: number)
      // Ideally backend returns pagination. Since we don't know the exact api structure, we call the existing one.
      const response: any = await api.master.getSubjects(
        boardFilter ? parseInt(boardFilter) : undefined,
        standardFilter ? parseInt(standardFilter) : undefined,
        mediumFilter ? parseInt(mediumFilter) : undefined,
      );
      
      const subjectData: Subject[] = response.data.subjects || [];
      
      // Client-side filtering as fallback if API doesn't support search param or streams on getSubjects
      let filtered = [...subjectData];
      if (searchQuery) {
        filtered = filtered.filter(s => s.name.toLowerCase().includes(searchQuery.toLowerCase()));
      }
      
      setTotalSubjects(filtered.length);
      
      // Client-side pagination
      const start = page * rowsPerPage;
      const paginated = filtered.slice(start, start + rowsPerPage);
      
      setSubjects(paginated);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load subjects');
      setSubjects([]);
    } finally {
      setLoading(false);
    }
  };

  const handleBoardChange = (value: string) => {
    setBoardFilter(value);
    setMediumFilter('');
    setStandardFilter('');
    setStreamFilter('');
    setMediums([]);
    setStandards([]);
    if (value) {
      loadMediums();
      loadStandards(value);
    }
  };

  const handleMediumChange = (value: string) => {
    setMediumFilter(value);
  };

  const handleStandardChange = (value: string) => {
    setStandardFilter(value);
    setStreamFilter('');
    loadStreams(value);
  };

  const handleDeleteClick = (subject: Subject, e: React.MouseEvent) => {
    e.stopPropagation();
    setSubjectToDelete(subject);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!subjectToDelete) return;
    try {
      setDeleting(true);
      if (api.master.deleteSubject) await api.master.deleteSubject(subjectToDelete.id);
      setDeleteDialogOpen(false);
      loadSubjects();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete subject');
    } finally {
      setDeleting(false);
      setSubjectToDelete(null);
    }
  };

  const handleAddClick = () => {
    setSubjectToEdit({
      board_id: boardFilter ? parseInt(boardFilter) : undefined,
      standard_id: standardFilter ? parseInt(standardFilter) : undefined,
      medium_id: mediumFilter ? parseInt(mediumFilter) : undefined,
    });
    setEditDialogOpen(true);
  };

  const handleEditClick = (subject: Subject, e: React.MouseEvent) => {
    e.stopPropagation();
    setSubjectToEdit({...subject});
    setEditDialogOpen(true);
  };

  const handleSaveSubject = async () => {
    if (!subjectToEdit.name) {
      alert('Name is required');
      return;
    }
    
    try {
      setSaving(true);
      if (subjectToEdit.id && api.master.updateSubject) {
        await api.master.updateSubject(subjectToEdit.id, subjectToEdit);
      } else if (api.master.createSubject) {
        await api.master.createSubject(subjectToEdit);
      } else {
        alert('API endpoints not implemented yet.');
      }
      setEditDialogOpen(false);
      loadSubjects();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save subject');
    } finally {
      setSaving(false);
    }
  };

  const handleSearch = () => {
    setPage(0);
    loadSubjects();
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Subjects
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage subjects across different boards and standards
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
        >
          Add Subject
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 3, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
        {/* Left Sidebar Filters */}
        <Box sx={{ width: { xs: '100%', md: '25%' }, flexShrink: 0 }}>
          <Paper elevation={2} sx={{ p: 2, position: 'sticky', top: 80 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Filters
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              
              <TextField
                size="small"
                placeholder="Search subjects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                InputProps={{
                  endAdornment: (
                    <IconButton size="small" onClick={handleSearch}>
                      <SearchIcon />
                    </IconButton>
                  )
                }}
              />

              <FormControl size="small" fullWidth>
                <InputLabel>Board</InputLabel>
                <Select
                  value={boardFilter}
                  label="Board"
                  onChange={(e) => handleBoardChange(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {boards.map((board) => (
                    <MenuItem key={board.id} value={board.id}>
                      {board.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth disabled={!boardFilter}>
                <InputLabel>Medium</InputLabel>
                <Select
                  value={mediumFilter}
                  label="Medium"
                  onChange={(e) => handleMediumChange(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {mediums.map((medium) => (
                    <MenuItem key={medium.id} value={medium.id}>
                      {medium.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth disabled={!boardFilter}>
                <InputLabel>Standard</InputLabel>
                <Select
                  value={standardFilter}
                  label="Standard"
                  onChange={(e) => handleStandardChange(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {standards.map((standard) => (
                    <MenuItem key={standard.id} value={standard.id}>
                      {standard.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth disabled={!standardFilter}>
                <InputLabel>Stream</InputLabel>
                <Select
                  value={streamFilter}
                  label="Stream"
                  onChange={(e) => setStreamFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {streams.map((stream) => (
                    <MenuItem key={stream.id} value={stream.id}>
                      {stream.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <Button 
                variant="outlined" 
                color="primary" 
                fullWidth 
                onClick={() => {
                  setBoardFilter('');
                  setMediumFilter('');
                  setStandardFilter('');
                  setStreamFilter('');
                  setSearchQuery('');
                  setPage(0);
                }}
              >
                Clear Filters
              </Button>
            </Box>
          </Paper>
        </Box>

        {/* Right Content Data Grid */}
        <Box sx={{ width: { xs: '100%', md: '75%' } }}>
          <TableContainer component={Paper} elevation={2}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Board</TableCell>
                      <TableCell>Standard</TableCell>
                      <TableCell>Medium</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {subjects.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                            No subjects found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      subjects.map((subject) => (
                        <TableRow key={subject.id} hover>
                          <TableCell>{subject.id}</TableCell>
                          <TableCell sx={{ fontWeight: 'medium' }}>{subject.name}</TableCell>
                          <TableCell>{subject.board_name || '-'}</TableCell>
                          <TableCell>{subject.standard_name || '-'}</TableCell>
                          <TableCell>{subject.medium_name || '-'}</TableCell>
                          <TableCell align="center">
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => handleEditClick(subject, e)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => handleDeleteClick(subject, e)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={totalSubjects}
                  page={page}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[10, 20, 50]}
                />
              </>
            )}
          </TableContainer>
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Subject</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this subject? This action cannot be undone.
          </Typography>
          {subjectToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {subjectToDelete.name}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{subjectToEdit.id ? 'Edit Subject' : 'Add New Subject'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Subject Name"
              fullWidth
              required
              value={subjectToEdit.name || ''}
              onChange={(e) => setSubjectToEdit({ ...subjectToEdit, name: e.target.value })}
            />
            {/* If backend requires these IDs on create, we expose them here as selects. For now we assume typical hierarchical payload. */}
            <FormControl fullWidth size="small">
              <InputLabel>Board</InputLabel>
              <Select
                value={subjectToEdit.board_id || ''}
                label="Board"
                onChange={(e) => setSubjectToEdit({ ...subjectToEdit, board_id: e.target.value as number })}
              >
                {boards.map(b => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Standard</InputLabel>
              <Select
                value={subjectToEdit.standard_id || ''}
                label="Standard"
                onChange={(e) => setSubjectToEdit({ ...subjectToEdit, standard_id: e.target.value as number })}
              >
                {standards.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveSubject} color="primary" variant="contained" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Subjects;
